const PFA = {
    _getEarlyAccessMap: function (customizerRoot) {
        const root = customizerRoot || document.querySelector('#popup-chat-customizer');
        const script = root?.querySelector('#raw-variant-early-access');
        if (!script?.textContent?.trim()) return null;
        try {
            const parsed = JSON.parse(script.textContent);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
        } catch {
            return null;
        }
    },

    _isVariantEarlyAccessRestricted: function (customizerRoot, variantId, earlyAccess) {
        if (customizerRoot?.dataset?.earlyAccessEligible === 'true') {
            return false;
        }
        const map = this._getEarlyAccessMap(customizerRoot);
        if (map) {
            return map[String(variantId)] === true;
        }
        return earlyAccess === true;
    },

    init: async function () {
        const customizerRoot = document.querySelector('#popup-chat-customizer');
        if (!customizerRoot) {
            return;
        }

        const injectedWrapper = customizerRoot.parentElement || document;

        const productHandle = customizerRoot.dataset.productHandle;

        let productData;
        try {
            productData = await Utility.fetchProductDataAjax(productHandle);
        } catch (error) {
            console.error('PFA, failed to fetch product data:', error);
            return;
        }

        const initialVariantId = customizerRoot.dataset.variantId;
        const anyAvailable = productData.variants.some(v => v.available);
        const addButton = injectedWrapper.querySelector('[data-customizer-add]');
        const restrictedMsg = injectedWrapper.querySelector('.popup-chat-customizer-restricted-msg');

        let initialEarlyAccess;
        if (!this._getEarlyAccessMap(customizerRoot)) {
            const details = await Utility.fetchProductDetails(productHandle, initialVariantId);
            initialEarlyAccess = details?.earlyAccess === true;
        }
        const isRestrictedEA = this._isVariantEarlyAccessRestricted(
            customizerRoot,
            initialVariantId,
            initialEarlyAccess
        );

        if (addButton) {
            if (isRestrictedEA) {
                addButton.classList.remove('out-of-stock-main');
                addButton.classList.add('disabled-btn', 'early-access-restricted');
                addButton.disabled = true;
                addButton.textContent = 'Early Access Only';
            } else if (!anyAvailable) {
                addButton.classList.add('out-of-stock-main', 'disabled-btn');
                addButton.classList.remove('early-access-restricted');
                addButton.disabled = true;
                addButton.textContent = 'Out of Stock';
            }

            if (restrictedMsg) {
                restrictedMsg.style.display = isRestrictedEA ? '' : 'none';
            }

            // The AJAX cart script likely stops propagation, hiding the click from the global listener.
            // We manually fire the ATC event directly at the source. This ensures it fires even
            // if the user clicks Add To Cart on the default variant without changing any swatches.
            addButton.addEventListener('click', () => {
                if (typeof fireEvent === 'function') {
                    fireEvent(addButton);
                }
            });
        }

        const zoomTrigger = customizerRoot.querySelector('[data-customizer-zoom-trigger]');
        const lightbox = customizerRoot.querySelector('[data-customizer-lightbox]');
        const lightboxClose = customizerRoot.querySelector('[data-customizer-lightbox-close]');
        const lightboxDismiss = customizerRoot.querySelector('[data-customizer-lightbox-dismiss]');

        if (zoomTrigger) {
            zoomTrigger.addEventListener('click', () => {
                this._openLightbox(customizerRoot);
            });
        }

        if (lightboxClose) {
            lightboxClose.addEventListener('click', () => {
                this._closeLightbox(customizerRoot);
            });
        }

        if (lightboxDismiss) {
            lightboxDismiss.addEventListener('click', () => {
                this._closeLightbox(customizerRoot);
            });
        }

        if (lightbox) {
            lightbox.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    // Don't let this propagate to the chat modal's own Escape handler.
                    event.stopPropagation();
                    this._closeLightbox(customizerRoot);
                } else if (event.key === 'Tab' && lightboxClose) {
                    // Close button is the only focusable element, so trap focus on it.
                    event.preventDefault();
                    event.stopPropagation();
                    lightboxClose.focus();
                }
            });
        }

        const context = {
            parent: injectedWrapper,
            productData: productData,
            onVariantChange: async (matchingVariant) => {
                await this._handleVariantChange(matchingVariant, customizerRoot, productData, injectedWrapper);
            }
        };

        const swatchButtons = Array.from(injectedWrapper.querySelectorAll('.swatch-btn'));
        swatchButtons.forEach((swatchButton) => {
            swatchButton.addEventListener('click', async (event) => {
                await Shared.handleSwatchChange(event.currentTarget, false, false, context);
            });
        });

        const optionSelects = Array.from(injectedWrapper.querySelectorAll('select'));
        optionSelects.forEach((selectElement) => {
            const hasSwatchOptions = selectElement.querySelector('option.swatch-btn,[data-option-name],[data-swatch-value]');
            if (!hasSwatchOptions) {
                return;
            }

            selectElement.addEventListener('change', async (event) => {
                // Find the currently selected <option> and manually fire its GA4 event
                const selectedOption = event.currentTarget.options[event.currentTarget.selectedIndex];
                if (selectedOption && selectedOption.hasAttribute('data-ga-event') && typeof fireEvent === 'function') {
                    fireEvent(selectedOption);
                }

                await Shared.handleSwatchChange(event.currentTarget, false, false, context);

                // Force the color swatches to re-evaluate based on the new Shape/Weight selection
                this._updateSwatchVisibility(productData, injectedWrapper);
            });
        });

        await new Promise((resolve) => requestAnimationFrame(resolve));

        const firstSelect = injectedWrapper.querySelector('select.swatch-btn-select') || injectedWrapper.querySelector('select');
        if (firstSelect) {
            await Shared.handleSwatchChange(firstSelect, false, false, context);
        } else {
            const initialSelectedSwatch = injectedWrapper.querySelector('.swatch-btn.selected');
            if (initialSelectedSwatch) {
                await Shared.handleSwatchChange(initialSelectedSwatch, false, false, context);
            }
        }
    },

    /**
     * Handles DOM updates when a new variant is selected.
     */
    _handleVariantChange: async function (matchingVariant, customizerRoot, productData, injectedWrapper) {
        // The variant input, add-to-cart button, and restricted message live in the pinned footer,
        // outside #popup-chat-customizer - see the matching note in init().
        const variantInput = injectedWrapper.querySelector('[data-customizer-variant-input]');
        if (variantInput) {
            variantInput.value = String(matchingVariant.id);
        }

        const addButton = injectedWrapper.querySelector('[data-customizer-add]');
        const restrictedMsg = injectedWrapper.querySelector('.popup-chat-customizer-restricted-msg');

        let earlyAccess;
        if (!this._getEarlyAccessMap(customizerRoot) && productData?.handle) {
            const details = await Utility.fetchProductDetails(productData.handle, matchingVariant.id);
            earlyAccess = details?.earlyAccess === true;
        }
        const isRestrictedEA = this._isVariantEarlyAccessRestricted(
            customizerRoot,
            matchingVariant.id,
            earlyAccess
        );

        if (addButton) {
            // Per-Variant Stock Check: Toggle classes based on current variant availability and restrictions
            const addButtonPriceText = Utility.formatLocalizedMoney(Number(matchingVariant.price) || 0);

            if (isRestrictedEA) {
                addButton.classList.remove('out-of-stock-main');
                addButton.classList.add('disabled-btn', 'early-access-restricted');
                addButton.disabled = true;
                addButton.textContent = 'Early Access Only';
            } else if (matchingVariant && matchingVariant.available) {
                addButton.classList.remove('out-of-stock-main', 'disabled-btn', 'early-access-restricted');
                addButton.disabled = false;
                addButton.textContent = `Add to cart - ${addButtonPriceText}`;
            } else {
                addButton.classList.add('out-of-stock-main', 'disabled-btn');
                addButton.classList.remove('early-access-restricted');
                addButton.disabled = true;
                addButton.textContent = 'Out of Stock';
            }

            if (restrictedMsg) {
                restrictedMsg.style.display = isRestrictedEA ? '' : 'none';
            }

            // GA4 Event tracking dynamically based on current variant selection
            // Logic: pop_prod_[Variant ID]_atc
            const variantId = String(matchingVariant.id);
            addButton.dataset.gaEvent = `pop_prod_${variantId}_atc`;

            // Update Title for GA4 reporting to include specific variant name
            const vTitle = matchingVariant.title && matchingVariant.title !== 'Default Title'
                ? ` - ${matchingVariant.title}`
                : '';
            addButton.dataset.title = `ATC: ${productData.title}${vTitle}`;

            addButton.dataset.variantId = variantId;
            addButton.dataset.variantTitle = matchingVariant.title && matchingVariant.title !== 'Default Title'
                ? matchingVariant.title
                : '';

            const nextImageUrl = matchingVariant.featured_image && matchingVariant.featured_image.src
                ? matchingVariant.featured_image.src
                : '';
            if (nextImageUrl) {
                addButton.dataset.productImageUrl = nextImageUrl;
            }
        }

        const imageElement = customizerRoot.querySelector('[data-customizer-image]');
        if (imageElement) {
            const nextImageSrc = matchingVariant.featured_image && matchingVariant.featured_image.src
                ? Utility.formatUrl(matchingVariant.featured_image.src, 500)
                : imageElement.dataset.fallbackImage;
            const nextZoomSrc = matchingVariant.featured_image && matchingVariant.featured_image.src
                ? Utility.formatUrl(matchingVariant.featured_image.src, 1200)
                : imageElement.dataset.zoomFallback;
            const nextImageAlt = (matchingVariant.featured_image && matchingVariant.featured_image.alt) || productData.title || '';

            if (nextZoomSrc) {
                imageElement.dataset.zoomImage = nextZoomSrc;
            }

            // First call isn't always the server-rendered variant (can get auto-corrected), so verify
            // by variant id, not image URL - the URL formats aren't guaranteed to match exactly.
            const isFirstSync = !imageElement.dataset.customizerImageReady;
            const matchesServerRenderedVariant = String(matchingVariant.id) === customizerRoot.dataset.variantId;
            imageElement.dataset.customizerImageReady = 'true';

            if (nextImageSrc && isFirstSync && matchesServerRenderedVariant) {
                imageElement.dataset.customizerImageSrc = nextImageSrc;
            } else if (nextImageSrc && imageElement.dataset.customizerImageSrc !== nextImageSrc) {
                imageElement.dataset.customizerImageSrc = nextImageSrc;
                imageElement.classList.add('is-loading');

                const preloadImage = new Image();
                preloadImage.onload = () => {
                    // Ignore if a newer variant change already replaced this preload.
                    if (imageElement.dataset.customizerImageSrc !== nextImageSrc) {
                        return;
                    }
                    imageElement.src = nextImageSrc;
                    imageElement.alt = nextImageAlt;
                    imageElement.classList.remove('is-loading');
                };
                preloadImage.onerror = () => {
                    if (imageElement.dataset.customizerImageSrc !== nextImageSrc) {
                        return;
                    }
                    imageElement.classList.remove('is-loading');
                };
                preloadImage.src = nextImageSrc;
            }
        }

        const selectedColorValueElement = customizerRoot.querySelector(
            '[data-customizer-color-label] .popup-chat-customizer__swatch-value'
        );

        if (selectedColorValueElement) {
            const selectedColorSwatch = customizerRoot.querySelector(
                '.swatch-btn.selected[data-option-name="color"]'
            );

            if (selectedColorSwatch) {
                selectedColorValueElement.textContent = selectedColorSwatch.dataset.swatchValue;
            }
        }

        this._updateSwatchVisibility(productData, injectedWrapper);
    },

    _prefersReducedMotion: function () {
        return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    /**
     * Opens the tap-to-enlarge lightbox with a shared-element (FLIP) transition: the image grows
     * from the thumbnail's exact on-screen position/size out to its full centered size, rather than
     * fading in already-centered. This keeps the full-viewport takeover feeling like an extension of
     * the thumbnail instead of a disconnected jump.
     */
    _openLightbox: function (customizerRoot) {
        const trigger = customizerRoot.querySelector('[data-customizer-zoom-trigger]');
        const imageElement = customizerRoot.querySelector('[data-customizer-image]');
        const lightbox = customizerRoot.querySelector('[data-customizer-lightbox]');
        const lightboxImage = customizerRoot.querySelector('[data-customizer-lightbox-image]');
        if (!trigger || !imageElement || !lightbox || !lightboxImage) {
            return;
        }

        const zoomSrc = imageElement.dataset.zoomImage || imageElement.dataset.zoomFallback || imageElement.src;
        const startRect = trigger.getBoundingClientRect();
        const reduceMotion = this._prefersReducedMotion();

        // Guards against a late image load reopening a lightbox the user already closed.
        const openToken = (this._lightboxOpenToken || 0) + 1;
        this._lightboxOpenToken = openToken;

        this._lightboxLastFocused = document.activeElement;
        // Save prior overflow (another modal may have already locked scroll) to restore on close.
        this._bodyOverflowBeforeLightbox = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        lightboxImage.alt = imageElement.alt;

        const focusCloseButton = () => {
            const closeButton = lightbox.querySelector('[data-customizer-lightbox-close]');
            if (closeButton) {
                closeButton.focus();
            }
        };

        const playFlip = () => {
            lightbox.hidden = false;

            if (reduceMotion) {
                lightbox.classList.add('is-open');
                focusCloseButton();
                return;
            }

            // First frame: measure the image's natural (final) rect, then jump it to LOOK like the
            // thumbnail via an inverted transform, with no transition, so the jump itself is invisible.
            requestAnimationFrame(() => {
                const endRect = lightboxImage.getBoundingClientRect();
                const deltaX = (startRect.left + startRect.width / 2) - (endRect.left + endRect.width / 2);
                const deltaY = (startRect.top + startRect.height / 2) - (endRect.top + endRect.height / 2);
                const scaleX = startRect.width / endRect.width;
                const scaleY = startRect.height / endRect.height;

                lightboxImage.style.transition = 'none';
                lightboxImage.style.borderRadius = '12px';
                lightboxImage.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`;

                // Second frame: remove the invert with a transition, so it animates from the
                // thumbnail's spot/size back to its natural full-size position. This is the "Play" step.
                requestAnimationFrame(() => {
                    lightbox.classList.add('is-open');
                    lightboxImage.style.transition = 'transform 0.32s cubic-bezier(0.16, 1, 0.3, 1), border-radius 0.32s ease';
                    lightboxImage.style.borderRadius = '16px';
                    lightboxImage.style.transform = 'translate(0, 0) scale(1, 1)';
                });
            });

            focusCloseButton();
        };

        if (zoomSrc && (lightboxImage.getAttribute('src') !== zoomSrc || !lightboxImage.complete)) {
            lightboxImage.classList.add('is-loading');
            lightboxImage.onload = () => {
                lightboxImage.classList.remove('is-loading');
                if (this._lightboxOpenToken !== openToken) {
                    return;
                }
                playFlip();
            };
            lightboxImage.onerror = () => {
                lightboxImage.classList.remove('is-loading');
                if (this._lightboxOpenToken !== openToken) {
                    return;
                }
                playFlip();
            };
            lightboxImage.src = zoomSrc;
        } else {
            playFlip();
        }
    },

    /**
     * Closes the lightbox by reversing the FLIP: the image shrinks back down to the thumbnail's
     * current position/size, then hides once that transition finishes.
     */
    _closeLightbox: function (customizerRoot) {
        // Invalidates any pending open, even if not visible yet - see _openLightbox.
        this._lightboxOpenToken = (this._lightboxOpenToken || 0) + 1;

        const lightbox = customizerRoot.querySelector('[data-customizer-lightbox]');
        const lightboxImage = customizerRoot.querySelector('[data-customizer-lightbox-image]');
        const trigger = customizerRoot.querySelector('[data-customizer-zoom-trigger]');
        if (!lightbox || lightbox.hidden) {
            return;
        }

        const reduceMotion = this._prefersReducedMotion();

        // Restore prior overflow instead of clearing it, in case another modal still needs it locked.
        document.body.style.overflow = this._bodyOverflowBeforeLightbox || '';
        lightbox.classList.remove('is-open');

        const finishClose = () => {
            lightbox.hidden = true;
            if (lightboxImage) {
                lightboxImage.style.transition = '';
                lightboxImage.style.transform = '';
                lightboxImage.style.borderRadius = '';
            }
        };

        if (!reduceMotion && lightboxImage && trigger) {
            const endRect = trigger.getBoundingClientRect();
            const startRect = lightboxImage.getBoundingClientRect();
            const deltaX = (endRect.left + endRect.width / 2) - (startRect.left + startRect.width / 2);
            const deltaY = (endRect.top + endRect.height / 2) - (startRect.top + startRect.height / 2);
            const scaleX = endRect.width / startRect.width;
            const scaleY = endRect.height / startRect.height;

            lightboxImage.style.transition = 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.28s ease';
            lightboxImage.style.borderRadius = '12px';
            lightboxImage.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scaleX}, ${scaleY})`;

            lightboxImage.addEventListener('transitionend', finishClose, { once: true });
        } else {
            finishClose();
        }

        if (this._lightboxLastFocused && typeof this._lightboxLastFocused.focus === 'function') {
            this._lightboxLastFocused.focus();
        }
    },

    /**
     * Evaluates valid option combinations and hides unavailable color swatches dynamically.
     * Automatically accounts for Shape, Weight, or any other non-color option in the dropdowns.
     */
    _updateSwatchVisibility: function (productData, injectedWrapper) {
        const normalize = (val) => String(val || "").toLowerCase().trim();

        // Find all visual Color swatches
        const colorSwatches = Array.from(injectedWrapper.querySelectorAll('.swatch-btn'))
            .filter(s => normalize(s.dataset.optionName) === 'color' && s.tagName.toLowerCase() !== 'option');

        if (colorSwatches.length === 0) return;

        // Gather all currently selected non-color values (Shape, Weight, etc.)
        const selectedNonColorValues = [];

        // Grab selections from dropdown selectors
        const activeSelects = injectedWrapper.querySelectorAll('select.swatch-btn-select');
        activeSelects.forEach(select => {
            const selectedOption = select.options[select.selectedIndex];
            if (selectedOption && normalize(selectedOption.dataset.optionName) !== 'color') {
                selectedNonColorValues.push(normalize(selectedOption.value));
            }
        });

        // (Fallback) Just in case we ever add non-color buttons in the future
        const activeButtons = injectedWrapper.querySelectorAll('.swatch-btn.selected');
        activeButtons.forEach(btn => {
            if (btn.tagName.toLowerCase() !== 'option' && normalize(btn.dataset.optionName) !== 'color') {
                selectedNonColorValues.push(normalize(btn.dataset.swatchValue));
            }
        });

        // Evaluate color visibility against all selected dropdown options
        let firstVisibleColor = null;
        let currentlySelectedIsHidden = false;

        colorSwatches.forEach(swatch => {
            const colorValue = normalize(swatch.dataset.swatchValue);

            const isValidCombination = productData.variants.some(variant => {
                const variantOptions = variant.options.map(opt => normalize(opt));

                if (!variantOptions.includes(colorValue)) return false;

                return selectedNonColorValues.every(val => variantOptions.includes(val));
            });

            if (isValidCombination) {
                swatch.style.setProperty('display', 'flex', 'important');
                swatch.classList.remove('hide-swatch');
                if (!firstVisibleColor) firstVisibleColor = swatch;
            } else {
                swatch.style.setProperty('display', 'none', 'important');
                swatch.classList.add('hide-swatch');
                if (swatch.classList.contains('selected')) {
                    currentlySelectedIsHidden = true;
                }
            }
        });

        // Auto-select the first valid color if the current selection becomes invalid
        if (currentlySelectedIsHidden && firstVisibleColor) {
            firstVisibleColor.click();
        }
    }
};

window.Jarvis = window.Jarvis || {};
window.Jarvis.PFA = PFA;